import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo from '../components/Seo';

const EFFECTIVE_DATE = 'January 1, 2026';

const TOC = [
  { id: 'who-we-are',     title: '1. Who We Are' },
  { id: 'what-we-collect',title: '2. Information We Collect' },
  { id: 'how-we-use',     title: '3. How We Use Your Information' },
  { id: 'how-we-share',   title: '4. How We Share Your Information' },
  { id: 'cookies',        title: '5. Cookies and Tracking' },
  { id: 'wallet-data',    title: '6. Wallet and On-Chain Data' },
  { id: 'retention',      title: '7. Data Retention' },
  { id: 'your-rights',    title: '8. Your Rights and Choices' },
  { id: 'security',       title: '9. Security' },
  { id: 'transfers',      title: '10. International Data Transfers' },
  { id: 'children',       title: '11. Children’s Privacy' },
  { id: 'changes',        title: '12. Changes to This Policy' },
  { id: 'contact',        title: '13. Contact Us' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-10">
      <h2 className="text-xl font-semibold text-white mb-3">{title}</h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-gray-300">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-dark-900 text-gray-200">
      <Seo
        title="Privacy Policy"
        description="How haythix collects, uses, and protects your data — including wallet addresses, on-chain activity, and account information. Effective January 1, 2026."
        path="/privacy"
      />
      <PublicNav />

      {/* Body */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-widest text-accent-primary/80 mb-2">Privacy Policy</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Privacy Policy</h1>
          <p className="text-sm text-gray-400">
            Effective date: <span className="text-gray-200">{EFFECTIVE_DATE}</span>
          </p>
        </div>

        {/* Table of contents */}
        <nav className="card border-dark-600 mb-12">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Contents</p>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {TOC.map(({ id, title }) => (
              <li key={id}>
                <a href={`#${id}`} className="text-gray-300 hover:text-accent-primary transition-colors">
                  {title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Intro */}
        <div className="space-y-3 text-[15px] leading-relaxed text-gray-300 mb-12">
          <p>
            This Privacy Policy describes how <strong>Borak Solutions LLC</strong>, the operator of
            the haythix trading platform (&ldquo;haythix,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), collects, uses,
            and shares information about you when you use our website at <em>haythix.com</em>, our
            related services, and any application programming interfaces, mobile applications, or
            connected services we provide (collectively, the &ldquo;Service&rdquo;).
          </p>
          <p>
            By using the Service, you agree to the practices described in this Policy. If you do
            not agree, please do not use the Service.
          </p>
        </div>

        <Section id="who-we-are" title="1. Who We Are">
          <p>
            haythix is a software platform that helps users analyze cryptocurrency markets,
            configure automated trading strategies, and connect their own self-custodial wallets
            and exchange accounts to execute trades. The Service is operated by Borak Solutions
            LLC, a limited liability company organized under the laws of the State of Wisconsin,
            United States of America.
          </p>
          <p>
            haythix is a software tool. We do not custody user funds, hold user assets, or execute
            trades on our own behalf. Trading actions are taken using credentials you provide or
            wallets you control.
          </p>
        </Section>

        <Section id="what-we-collect" title="2. Information We Collect">
          <p>We collect the following categories of information:</p>
          <p className="text-white font-medium pt-2">a. Information you provide directly</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Account information:</strong> name, email address, password (stored as a one-way hash), and optional profile details such as a display name or avatar.</li>
            <li><strong>Authentication identifiers:</strong> if you sign in with Google, we receive your verified email address, name, profile picture URL, and a Google-issued user identifier.</li>
            <li><strong>Subscription and payment information:</strong> processed by our payment processor, Stripe. We do not store full card numbers; we store only a Stripe customer identifier and the metadata Stripe returns about your subscription tier and renewal status.</li>
            <li><strong>Trading configuration:</strong> the strategies you configure, the markets you watch, the alert rules you set, and the journal entries you record.</li>
            <li><strong>Connected exchange API keys:</strong> if you choose to connect a centralized exchange account, the API key and secret you provide are stored encrypted at rest using AES-256-GCM. You can revoke access at any time from your settings.</li>
            <li><strong>Support communications:</strong> if you contact us, we keep a record of the correspondence.</li>
          </ul>
          <p className="text-white font-medium pt-2">b. Information collected automatically</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Usage data:</strong> the pages you view, features you interact with, and the timing of those events.</li>
            <li><strong>Device and connection data:</strong> IP address, browser type, operating system, time zone, and approximate location derived from your IP.</li>
            <li><strong>Diagnostic logs:</strong> error reports and performance traces recorded by Microsoft Application Insights to help us keep the Service reliable.</li>
          </ul>
          <p className="text-white font-medium pt-2">c. Information from third parties</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Market data:</strong> prices, on-chain data, and exchange data we retrieve from public APIs such as DexScreener, CoinGecko, and exchange public endpoints. This data is not personal to you.</li>
            <li><strong>Wallet addresses you connect:</strong> if you connect a self-custodial wallet, we read public blockchain data associated with the address you choose to share. We do not receive your private keys.</li>
          </ul>
        </Section>

        <Section id="how-we-use" title="3. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Provide, maintain, and improve the Service;</li>
            <li>Authenticate you, authorize access to features, and protect your account;</li>
            <li>Process subscription payments and manage trial periods;</li>
            <li>Send transactional messages such as welcome emails, password resets, billing receipts, and important Service updates;</li>
            <li>Detect, investigate, and prevent fraud, abuse, or security incidents;</li>
            <li>Comply with applicable legal obligations and respond to lawful requests; and</li>
            <li>Analyze usage in aggregated, de-identified form to understand product performance.</li>
          </ul>
          <p>
            We do not sell your personal information, and we do not use your information to train
            machine-learning models that benefit third parties.
          </p>
        </Section>

        <Section id="how-we-share" title="4. How We Share Your Information">
          <p>We share information with:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Service providers</strong> who process information on our behalf under written contracts that limit how they may use it. Today these include Microsoft Azure (hosting, storage, email delivery), Stripe (payments), Google (sign-in), DexScreener and CoinGecko (market data), and exchange APIs you choose to connect.</li>
            <li><strong>Other users</strong>, only when you choose to make information public, such as a public profile, a published strategy in the marketplace, or a community post.</li>
            <li><strong>Authorities and other parties</strong> when we believe in good faith that disclosure is required to comply with a subpoena, court order, or other valid legal process, or to protect the rights, property, or safety of haythix, our users, or others.</li>
            <li><strong>A successor</strong> in the event of a merger, acquisition, financing, reorganization, bankruptcy, or sale of assets, in which case the recipient will be required to honor this Policy with respect to information transferred.</li>
          </ul>
        </Section>

        <Section id="cookies" title="5. Cookies and Tracking">
          <p>
            We use a minimal set of cookies and similar technologies. We use a session token stored
            in your browser&rsquo;s local storage to keep you signed in, and we use first-party diagnostic
            cookies to detect errors. We do not use third-party advertising cookies, retargeting
            pixels, or cross-site tracking. You can clear cookies and local storage at any time in
            your browser settings; doing so will sign you out.
          </p>
        </Section>

        <Section id="wallet-data" title="6. Wallet and On-Chain Data">
          <p>
            When you connect a self-custodial wallet, we read public on-chain data associated with
            the address you provide (balances, transaction history, token holdings). This data is
            already publicly visible on the blockchain. We do not receive, request, or store your
            private keys, seed phrases, or signing credentials. When you initiate a transaction,
            the transaction is signed in your wallet on your device and broadcast to the relevant
            network; haythix has no ability to move funds without your explicit, signed approval.
          </p>
          <p>
            Blockchain transactions are public and irreversible. Once an on-chain action is
            broadcast, neither we nor you can delete it.
          </p>
        </Section>

        <Section id="retention" title="7. Data Retention">
          <p>
            We retain your account information for as long as your account is active and for a
            reasonable period afterward to comply with legal obligations, resolve disputes, and
            enforce our agreements. Diagnostic logs are typically retained for ninety (90) days.
            You may request deletion of your account at any time using the contact details below;
            we will delete or anonymize your information except where retention is required by
            law.
          </p>
        </Section>

        <Section id="your-rights" title="8. Your Rights and Choices">
          <p>
            Depending on where you live, you may have the following rights with respect to your
            personal information:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Access:</strong> request a copy of the information we hold about you.</li>
            <li><strong>Correction:</strong> ask us to correct information that is inaccurate or incomplete.</li>
            <li><strong>Deletion:</strong> ask us to delete your account and associated information.</li>
            <li><strong>Portability:</strong> request your information in a machine-readable format.</li>
            <li><strong>Objection or restriction:</strong> object to certain uses of your information or ask us to restrict processing.</li>
            <li><strong>Withdraw consent:</strong> where processing is based on your consent, you may withdraw it at any time.</li>
          </ul>
          <p>
            You can exercise most of these rights directly from your account settings, or by
            contacting us at the email address below. We will respond within the time frame
            required by applicable law.
          </p>
          <p>
            <strong>For California residents:</strong> we do not sell or share personal information
            as those terms are defined in the California Consumer Privacy Act (CCPA).
          </p>
        </Section>

        <Section id="security" title="9. Security">
          <p>
            We use industry-standard safeguards to protect your information, including HTTPS for
            all data in transit, encryption at rest for sensitive data, hashed passwords, AES-256
            encryption for stored exchange API keys, optional two-factor authentication, and
            access controls limiting which staff can view production data. No system is perfectly
            secure; we cannot guarantee absolute security and you use the Service at your own
            risk. If we become aware of a security incident affecting your information, we will
            notify you and the relevant authorities as required by law.
          </p>
        </Section>

        <Section id="transfers" title="10. International Data Transfers">
          <p>
            Our infrastructure is operated in the United States. If you access the Service from
            outside the United States, your information will be transferred to, stored, and
            processed in the United States, which may have different data protection laws than
            your country of residence. Where required, we rely on appropriate safeguards such as
            standard contractual clauses for cross-border transfers.
          </p>
        </Section>

        <Section id="children" title="11. Children’s Privacy">
          <p>
            The Service is not directed to children under the age of eighteen (18), and we do not
            knowingly collect information from anyone under that age. If you believe a child has
            provided us with personal information, please contact us and we will delete it.
          </p>
        </Section>

        <Section id="changes" title="12. Changes to This Policy">
          <p>
            We may update this Policy from time to time. When we do, we will post the updated
            version on this page and update the effective date at the top. If the changes are
            material, we will notify you by email or by a prominent notice in the Service before
            the changes take effect. Your continued use of the Service after the effective date
            constitutes acceptance of the updated Policy.
          </p>
        </Section>

        <Section id="contact" title="13. Contact Us">
          <p>If you have questions about this Policy or about how we handle your information, contact us at:</p>
          <p>
            <strong>Borak Solutions LLC</strong><br />
            State of Wisconsin, United States of America<br />
            Email: <a href="mailto:info@haythix.com" className="text-accent-primary hover:underline">info@haythix.com</a>
          </p>
        </Section>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-dark-700 text-center text-xs text-gray-500">
          <p>
            <Link to="/terms" className="hover:text-accent-primary transition-colors">Terms of Service</Link>
            <span className="mx-2">&middot;</span>
            <Link to="/privacy" className="hover:text-accent-primary transition-colors">Privacy Policy</Link>
          </p>
          <p className="mt-3">&copy; {new Date().getFullYear()} Borak Solutions LLC. All rights reserved.</p>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
